import { toaster } from '@/components/ui/toaster';
import { useAuth } from '@/contexts/auth-context';
import { usePusher } from '@/hooks/usePusher';
import useExportsStore from '@/stores/exports-store';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { ExportFailedEvent, ExportReadyEvent } from './types';

const AUTO_DISMISS_DELAY_MS = 8000;

/**
 * Global manager for async export notifications.
 *
 * Subscribes to the authenticated user's private Pusher channel and reacts to
 * `export:ready` / `export:failed` events. Updates the shared exports store
 * and renders a toast with a "Download" action trigger.
 *
 * Mount once at the authenticated layout root.
 */
export const ExportProgressToast: React.FC = () => {
  const { t } = useTranslation();
  const { user, isAuthenticated } = useAuth();

  const markReady = useExportsStore((state) => state.markReady);
  const markFailed = useExportsStore((state) => state.markFailed);
  const remove = useExportsStore((state) => state.remove);

  // Track exports we've already toasted to avoid duplicate notifications.
  const notifiedRef = useRef<Set<string>>(new Set());

  const channelName = isAuthenticated && user?.id ? `private-user-${user.id}` : '';

  const handleReady = useCallback(
    (data: ExportReadyEvent) => {
      if (!data?.exportId || notifiedRef.current.has(data.exportId)) return;
      notifiedRef.current.add(data.exportId);

      markReady(data.exportId, data.downloadUrl, data.fileName);

      toaster.create({
        title: t('exports.ready'),
        description: data.fileName,
        type: 'success',
        duration: AUTO_DISMISS_DELAY_MS,
        action: {
          label: t('exports.download'),
          onClick: () => {
            window.open(data.downloadUrl, '_blank', 'noopener,noreferrer');
            // Once the user clicks download, the export entry can be cleaned up.
            remove(data.exportId);
          },
        },
      });
    },
    [markReady, remove, t]
  );

  const handleFailed = useCallback(
    (data: ExportFailedEvent) => {
      if (!data?.exportId || notifiedRef.current.has(data.exportId)) return;
      notifiedRef.current.add(data.exportId);

      markFailed(data.exportId);

      toaster.create({
        title: t('exports.failed'),
        description: data.reason,
        type: 'error',
        duration: AUTO_DISMISS_DELAY_MS,
      });
    },
    [markFailed, t]
  );

  // Subscribe to ready events on the user's private channel.
  usePusher({
    channelName,
    eventName: 'export:ready',
    callback: handleReady,
    enabled: isAuthenticated && !!channelName,
  });

  // Subscribe to failed events on the same channel.
  usePusher({
    channelName,
    eventName: 'export:failed',
    callback: handleFailed,
    enabled: isAuthenticated && !!channelName,
  });

  // Clear the notified set whenever the user changes.
  useEffect(() => {
    notifiedRef.current = new Set();
  }, [user?.id]);

  return null;
};

export default ExportProgressToast;
